import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Rocket, X } from 'lucide-react';
import { labApi } from '@/lib/api';
import { VoicePicker, AvatarPicker, type ProductionVoiceOption, type ProductionAvatarOption } from './ProductionPickers';

// Fabryka wariantów: jeden zwycięski skrypt → N gotowych kreacji.
// Kreacja 1 = produkcja oryginału, 2..N = nowy hook + pełny łańcuch + produkcja.
// Wszystko audio-first: jeden głos ElevenLabs w każdej kreacji.

interface CampaignModalProps {
  productId: string;
  contentId: string;
  contentTitle: string;
  onStarted: (summary: { started: number; errors: string[] }) => void;
  onClose: () => void;
}

// Orientacyjny koszt HeyGen per kreacja (minuta materiału)
const COST_PER_CREATIVE = { final: 4, test: 1 };

export function CampaignModal({ productId, contentId, contentTitle, onStarted, onClose }: CampaignModalProps) {
  const [creatives, setCreatives] = useState(3);
  const [quality, setQuality] = useState<'test' | 'final'>('final');
  const [voices, setVoices] = useState<ProductionVoiceOption[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [avatars, setAvatars] = useState<ProductionAvatarOption[]>([]);
  const [avatarId, setAvatarId] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Głosy: ElevenLabs (audio-first); postać-fallback: avatary HeyGen/biblioteka
        const [voicesRes, avatarsRes] = await Promise.all([
          labApi.getVideoProductionOptions('elevenlabs'),
          labApi.getVideoProductionOptions('heygen'),
        ]);
        if (cancelled) return;
        const voiceList = voicesRes.data.data?.voices || [];
        setVoices(voiceList);
        if (voiceList.length > 0) setVoiceId(voiceList[0].voiceId);
        const avatarList = avatarsRes.data.data?.avatars || [];
        setAvatars(avatarList);
        const library = avatarList.find((avatar: any) => avatar.library);
        if (library?.avatarId) setAvatarId(library.avatarId);
        else if (avatarList[0]?.avatarId) setAvatarId(avatarList[0].avatarId);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || 'Nie udało się pobrać opcji. Sprawdź integracje ElevenLabs i HeyGen.');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const estimated = creatives * COST_PER_CREATIVE[quality];

  const handleStart = async () => {
    setError(null);
    setStarting(true);
    try {
      const res = await labApi.runCreativeCampaign(productId, contentId, {
        creatives,
        voiceId,
        avatarId: avatarId || undefined,
        voiceSource: 'elevenlabs',
        quality,
      });
      const data = res.data.data || {};
      const started = (data.originalProductionId ? 1 : 0) + (data.variantIds?.length || 0);
      onStarted({ started, errors: data.errors || [] });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się wystartować kampanii.');
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary">Kampania kreacji</h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          {contentTitle}: kreacja 1 = produkcja tego skryptu, kolejne = nowy hook + pełny łańcuch + produkcja. Jeden głos ElevenLabs w każdej kreacji, b-rolle bierzesz raz z Banku wideo.
        </p>

        {loadingOptions ? (
          <div className="flex items-center gap-2 py-6 text-xs text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Pobieranie głosów i postaci...
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-text-secondary mb-1.5 block">Liczba kreacji</span>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map(count => (
                  <button
                    key={count}
                    type="button"
                    onClick={() => setCreatives(count)}
                    className={`w-10 h-10 rounded-lg border text-sm font-semibold transition-all ${creatives === count ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary' : 'border-border bg-surface-hover/30 text-text-secondary hover:border-border-strong'}`}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs font-medium text-text-secondary mb-1.5 block">Jakość</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setQuality('final')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${quality === 'final' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                >
                  <span className={`text-xs font-medium block ${quality === 'final' ? 'text-accent-primary' : 'text-text-primary'}`}>Premium</span>
                  <span className="text-[10px] text-text-muted block mt-0.5">Avatar IV, 1080p, ok. $4/min</span>
                </button>
                <button
                  type="button"
                  onClick={() => setQuality('test')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${quality === 'test' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                >
                  <span className={`text-xs font-medium block ${quality === 'test' ? 'text-accent-primary' : 'text-text-primary'}`}>Testowa</span>
                  <span className="text-[10px] text-text-muted block mt-0.5">Avatar III, 720p, ok. $1/min</span>
                </button>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium text-text-secondary mb-1.5 block">Głos (ElevenLabs, wspólny dla wszystkich kreacji)</span>
              <VoicePicker idPrefix="campaign" voices={voices} value={voiceId} onChange={setVoiceId} />
            </div>

            <div>
              <span className="text-xs font-medium text-text-secondary mb-1.5 block">Postać (fallback dla scen bez przypisanego ujęcia)</span>
              <AvatarPicker idPrefix="campaign" avatars={avatars} value={avatarId} onChange={setAvatarId} />
            </div>

            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-info/5 border border-accent-info/15 text-[11px] text-text-secondary">
              <AlertCircle className="w-3.5 h-3.5 text-accent-info shrink-0 mt-0.5" />
              Szacunkowy koszt HeyGen: ~${estimated} ({creatives} × ${COST_PER_CREATIVE[quality]}/min materiału) + tokeny AI za rehooki i łańcuchy. Kreacje generują się w tle - obserwuj foldery produkcji.
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleStart}
            disabled={starting || loadingOptions || !voiceId}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
            Start kampanii
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
